import React, { useState, useEffect } from 'react';
import Form from 'react-bootstrap/Form';

// Define the types for our component based on the BigQuery-like structure
interface TableColumn {
  name: string;
  type: string;
  mode?: string;
  description?: string | null;
}

interface Table {
  columns: TableColumn[];
}

interface Dataset {
  [tableName: string]: Table;
}

interface Project {
  [datasetName: string]: Dataset;
}

interface TableStructure {
  [projectId: string]: Project;
}

interface TableMapping {
  id: string;
  name: string;
  table: string; // Fully qualified table name: "project.dataset.table"
  columns: string[];
  datetime_key: string;
  availableColumns?: TableColumn[];
}

interface TableMappingFormProps {
  tableMappings: Omit<TableMapping, 'availableColumns'>[];
  onAdd?: (mapping: Omit<TableMapping, 'id' | 'availableColumns'>) => void;
  onUpdate?: (id: string, mapping: Omit<TableMapping, 'id' | 'availableColumns'>) => void;
  onRemove?: (id: string) => void;
  tableStructure?: TableStructure;
}

const TableMappingForm: React.FC<TableMappingFormProps> = ({ 
  tableMappings = [],
  onAdd,
  onUpdate,
  onRemove,
  tableStructure
}) => {
  // Use provided table structure or null
  const tables = tableStructure || null;
  
  // State for hierarchical selection
  const [selectedProject, setSelectedProject] = useState<string>('');
  const [selectedDataset, setSelectedDataset] = useState<string>('');
  const [selectedTable, setSelectedTable] = useState<string>('');
  const [selectedTableColumns, setSelectedTableColumns] = useState<TableColumn[]>([]);
  const [newMappingName, setNewMappingName] = useState('');
  const [selectedColumns, setSelectedColumns] = useState<string[]>([]);
  const [selectedDatetimeKey, setSelectedDatetimeKey] = useState('');

  // State for tracking if we're in edit mode
  const [editMode, setEditMode] = useState(false);
  const [editingMappingId, setEditingMappingId] = useState<string | null>(null);

  // Function to get columns for a fully qualified table name (project.dataset.table)
  const getTableColumns = (fullTableName: string): TableColumn[] => {
    if (!fullTableName || !tables) return [];
    
    const [projectId, datasetName, tableName] = fullTableName.split('.');
    if (!projectId || !datasetName || !tableName) return [];
    
    try {
      return tables[projectId][datasetName][tableName].columns;
    } catch (error) {
      console.error(`Table not found: ${fullTableName}`);
      return [];
    }
  };

  // Parse a fully qualified table name into its components
  const parseFullyQualifiedTableName = (fullTableName: string): { projectId: string; datasetName: string; tableName: string } | null => {
    const parts = fullTableName.split('.');
    if (parts.length !== 3) return null;
    
    return {
      projectId: parts[0],
      datasetName: parts[1],
      tableName: parts[2]
    };
  };

  // Create flat list of all available tables for display in selection UI
  const getFullyQualifiedTableName = (): string => {
    if (selectedProject && selectedDataset && selectedTable) {
      return `${selectedProject}.${selectedDataset}.${selectedTable}`;
    }
    return '';
  };

  // Update available columns when selected table hierarchy changes
  useEffect(() => {
    const fullTableName = getFullyQualifiedTableName();
    if (fullTableName) {
      const columns = getTableColumns(fullTableName);
      setSelectedTableColumns(columns);
      
      // Only reset columns and datetime if not in edit mode
      // This prevents wiping out the pre-selected values when editing
      if (!editMode) {
        setSelectedColumns([]);
        setSelectedDatetimeKey('');
        
        // Default the mapping name to the table name
        setNewMappingName(selectedTable);
      }
    } else {
      setSelectedTableColumns([]);
    }
  }, [selectedProject, selectedDataset, selectedTable, editMode]);

  const handleProjectChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const projectId = e.target.value;
    setSelectedProject(projectId);
    setSelectedDataset('');
    setSelectedTable('');
  };

  const handleDatasetChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const datasetName = e.target.value;
    setSelectedDataset(datasetName);
    setSelectedTable('');
  };

  const handleTableChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const tableName = e.target.value;
    setSelectedTable(tableName);
  };

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setNewMappingName(e.target.value);
  };

  const handleColumnToggle = (columnName: string) => {
    setSelectedColumns(prev => 
      prev.includes(columnName)
        ? prev.filter(col => col !== columnName)
        : [...prev, columnName]
    );
  };

  const handleDatetimeKeyChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedDatetimeKey(e.target.value);
  };

  // Handle editing an existing mapping
  const handleEditMapping = (mappingId: string) => {
    const mappingToEdit = tableMappings.find(m => m.id === mappingId);
    if (!mappingToEdit) return;

    // Parse the fully qualified table name
    const tableInfo = parseFullyQualifiedTableName(mappingToEdit.table);
    if (!tableInfo) return;

    // Set form state for editing
    setSelectedProject(tableInfo.projectId);
    setSelectedDataset(tableInfo.datasetName);
    setSelectedTable(tableInfo.tableName);
    setNewMappingName(mappingToEdit.name);
    setSelectedColumns(mappingToEdit.columns);
    setSelectedDatetimeKey(mappingToEdit.datetime_key);
    
    // Set edit mode
    setEditMode(true);
    setEditingMappingId(mappingId);
  };

  // Update an existing mapping
  const handleUpdateMapping = () => {
    if (!editingMappingId || !onUpdate) return;
    
    const fullTableName = getFullyQualifiedTableName();
    
    if (!fullTableName || !newMappingName || selectedColumns.length === 0 || !selectedDatetimeKey) {
      alert('Please fill in all fields');
      return;
    }

    // Check for duplicate mapping name (excluding the current mapping being edited)
    const isDuplicateName = tableMappings.some(mapping => 
      mapping.id !== editingMappingId && 
      mapping.name.toLowerCase() === newMappingName.toLowerCase()
    );

    if (isDuplicateName) {
      alert(`A mapping with the name "${newMappingName}" already exists. Please use a unique name.`);
      return;
    }

    // Call the onUpdate prop with the updated mapping
    onUpdate(editingMappingId, {
      name: newMappingName,
      table: fullTableName,
      columns: selectedColumns,
      datetime_key: selectedDatetimeKey
    });

    // Reset form and exit edit mode
    resetForm();
  };

  const handleAddMapping = () => {
    if (!onAdd) return;
    
    const fullTableName = getFullyQualifiedTableName();
    
    if (!fullTableName || !newMappingName || selectedColumns.length === 0 || !selectedDatetimeKey) {
      alert('Please fill in all fields');
      return;
    }

    // Check for duplicate mapping name
    const isDuplicateName = tableMappings.some(mapping => 
      mapping.name.toLowerCase() === newMappingName.toLowerCase()
    );

    if (isDuplicateName) {
      alert(`A mapping with the name "${newMappingName}" already exists. Please use a unique name.`);
      return;
    }

    // Call the onAdd prop with the new mapping
    onAdd({
      name: newMappingName,
      table: fullTableName,
      columns: selectedColumns,
      datetime_key: selectedDatetimeKey
    });
    
    // Reset form
    resetForm();
  };

  const handleRemoveMapping = (id: string) => {
    if (onRemove) {
      onRemove(id);
    }
  };

  // Cancel editing
  const handleCancelEdit = () => {
    resetForm();
  };

  // Reset the form and exit edit mode
  const resetForm = () => {
    setSelectedProject('');
    setSelectedDataset('');
    setSelectedTable('');
    setNewMappingName('');
    setSelectedColumns([]);
    setSelectedDatetimeKey('');
    setEditMode(false);
    setEditingMappingId(null);
  };

  return (
    <div className="table-mapping-container" style={{ fontFamily: 'Arial, sans-serif', maxWidth: '800px', margin: '0 auto' }}>
      {/* Display existing mappings */}
      {tableMappings.length > 0 && (
        <div className="existing-mappings" style={{ marginBottom: '20px' }}>
          <h3>Existing Mappings</h3>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ backgroundColor: '#f2f2f2' }}>
                  <th style={{ padding: '8px', textAlign: 'left', border: '1px solid #ddd' }}>Name</th>
                  <th style={{ padding: '8px', textAlign: 'left', border: '1px solid #ddd' }}>Table</th>
                  <th style={{ padding: '8px', textAlign: 'left', border: '1px solid #ddd' }}>Columns</th>
                  <th style={{ padding: '8px', textAlign: 'left', border: '1px solid #ddd' }}>Datetime Key</th>
                  <th style={{ padding: '8px', textAlign: 'left', border: '1px solid #ddd' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {tableMappings.map(mapping => (
                  <tr key={mapping.id} style={{ borderBottom: '1px solid #ddd' }}>
                    <td style={{ padding: '8px', border: '1px solid #ddd' }}>{mapping.name}</td>
                    <td style={{ padding: '8px', border: '1px solid #ddd' }}>{mapping.table}</td>
                    <td style={{ padding: '8px', border: '1px solid #ddd' }}>{mapping.columns.join(', ')}</td>
                    <td style={{ padding: '8px', border: '1px solid #ddd' }}>{mapping.datetime_key}</td>
                    <td style={{ padding: '8px', border: '1px solid #ddd' }}>
                      <div style={{ display: 'flex', gap: '5px' }}>
                        <button 
                          onClick={() => handleEditMapping(mapping.id)}
                          className='btn btn-outline-secondary'
                        >
                          Edit
                        </button>
                        <button 
                          onClick={() => handleRemoveMapping(mapping.id)}
                          className='btn btn-outline-danger'
                        >
                          Remove
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
      
      {/* Form to add/edit mapping - only show if tables are available */}
      {tables ? (
        <div className="add-mapping-form" style={{ backgroundColor: '#f9f9f9', padding: '15px', borderRadius: '5px' }}>
          <h3>{editMode ? 'Edit Table Mapping' : 'Add New Table Mapping'}</h3>
          
          {/* Hierarchical selection */}
          <div style={{ marginBottom: '10px' }}>
            <label htmlFor="project-select" style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
              Select Project:
            </label>
            <select
              id="project-select"
              value={selectedProject}
              onChange={handleProjectChange}
              style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ddd' }}
            >
              <option value="">-- Select a project --</option>
              {tables && Object.keys(tables).map(projectId => (
                <option key={projectId} value={projectId}>
                  {projectId}
                </option>
              ))}
            </select>
          </div>
          
          {selectedProject && (
            <div style={{ marginBottom: '10px' }}>
              <label htmlFor="dataset-select" style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                Select Dataset:
              </label>
              <select
                id="dataset-select"
                value={selectedDataset}
                onChange={handleDatasetChange}
                style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ddd' }}
              >
                <option value="">-- Select a dataset --</option>
                {tables && Object.keys(tables[selectedProject] || {}).map(datasetName => (
                  <option key={datasetName} value={datasetName}>
                    {datasetName}
                  </option>
                ))}
              </select>
            </div>
          )}
          
          {selectedProject && selectedDataset && (
            <div style={{ marginBottom: '10px' }}>
              <label htmlFor="table-select" style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                Select Table:
              </label>
              <select
                id="table-select"
                value={selectedTable}
                onChange={handleTableChange}
                style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ddd' }}
              >
                <option value="">-- Select a table --</option>
                {tables && Object.keys(tables[selectedProject][selectedDataset] || {}).map(tableName => (
                  <option key={tableName} value={tableName}>
                    {tableName}
                  </option>
                ))}
              </select>
            </div>
          )}
          
          {/* Only show mapping name after table is selected */}
          {selectedProject && selectedDataset && selectedTable && (
            <div style={{ marginBottom: '10px' }}>
              <label htmlFor="mapping-name" style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                Mapping Name:
              </label>
              <input
                id="mapping-name"
                type="text"
                value={newMappingName}
                onChange={handleNameChange}
                placeholder="Enter a name for this mapping"
                style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ddd' }}
              />
            </div>
          )}
          
          {selectedTableColumns.length > 0 && (
            <>
              <div style={{ marginBottom: '10px' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                  Select Columns:
                </label>
                <div style={{ 
                  display: 'grid', 
                  gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', 
                  gap: '10px',
                  maxHeight: '200px',
                  overflowY: 'auto',
                  padding: '10px',
                  border: '1px solid #ddd',
                  borderRadius: '4px'
                }}>
                  {selectedTableColumns.map(column => (
                    <div key={column.name} style={{ display: 'flex', alignItems: 'center' }}>
                      <input
                        id={`column-${column.name}`}
                        type="checkbox"
                        checked={selectedColumns.includes(column.name)}
                        onChange={() => handleColumnToggle(column.name)}
                        style={{ marginRight: '5px' }}
                      />
                      <label htmlFor={`column-${column.name}`}>
                        {column.name} ({column.type})
                      </label>
                    </div>
                  ))}
                </div>
              </div>
              
              <div style={{ marginBottom: '15px' }}>
                <label htmlFor="datetime-key" style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                  Datetime Key for Replication:
                </label>
                <select
                  id="datetime-key"
                  value={selectedDatetimeKey}
                  onChange={handleDatetimeKeyChange}
                  style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ddd' }}
                >
                  <option value="">-- Select a datetime column --</option>
                  {selectedTableColumns
                    .filter(column => 
                      column.type?.toLowerCase().includes('time') || 
                      column.type?.toLowerCase().includes('date')
                    )
                    .map(column => (
                      <option key={column.name} value={column.name}>
                        {column.name} ({column.type})
                      </option>
                    ))
                  }
                </select>
              </div>
            </>
          )}
          
          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '15px' }}>
            {editMode ? (
              <>
                <button
                  onClick={handleUpdateMapping}
                  disabled={!selectedProject || !selectedDataset || !selectedTable || !newMappingName || selectedColumns.length === 0 || !selectedDatetimeKey || !onUpdate}
                  style={{ 
                    backgroundColor: (!selectedProject || !selectedDataset || !selectedTable || !newMappingName || selectedColumns.length === 0 || !selectedDatetimeKey || !onUpdate) ? '#cccccc' : '#4CAF50', 
                    color: 'white', 
                    border: 'none', 
                    padding: '10px 15px', 
                    borderRadius: '4px', 
                    cursor: (!selectedProject || !selectedDataset || !selectedTable || !newMappingName || selectedColumns.length === 0 || !selectedDatetimeKey || !onUpdate) ? 'not-allowed' : 'pointer'
                  }}
                >
                  Update Mapping
                </button>
                
                <button
                  onClick={handleCancelEdit}
                  style={{ 
                    backgroundColor: '#f44336', 
                    color: 'white', 
                    border: 'none', 
                    padding: '10px 15px', 
                    borderRadius: '4px', 
                    cursor: 'pointer'
                  }}
                >
                  Cancel
                </button>
              </>
            ) : (
              <button
                onClick={handleAddMapping}
                disabled={!selectedProject || !selectedDataset || !selectedTable || !newMappingName || selectedColumns.length === 0 || !selectedDatetimeKey || !onAdd}
                style={{ 
                  backgroundColor: (!selectedProject || !selectedDataset || !selectedTable || !newMappingName || selectedColumns.length === 0 || !selectedDatetimeKey || !onAdd) ? '#cccccc' : '#4CAF50', 
                  color: 'white', 
                  border: 'none', 
                  padding: '10px 15px', 
                  borderRadius: '4px', 
                  cursor: (!selectedProject || !selectedDataset || !selectedTable || !newMappingName || selectedColumns.length === 0 || !selectedDatetimeKey || !onAdd) ? 'not-allowed' : 'pointer'
                }}
              >
                Add Mapping
              </button>
            )}
          </div>
        </div>
      ) : (
        <div className="add-mapping-form" style={{ backgroundColor: '#f9f9f9', padding: '15px', borderRadius: '5px', textAlign: 'center' }}>
          <p style={{ color: '#721c24', marginBottom: '10px' }}>
            <strong>Service credentials not configured.</strong>
          </p>
          <p>
            Please set up your BigQuery service credentials to enable table mapping configuration.
          </p>
        </div>
      )}
    </div>
  );
};

// BigQueryTableMappingWrapper component that handles the JSON serialization/deserialization
interface BigQueryTableMappingProps {
  value: string;
  onChange: (value: string) => void;
  label: string;
  tableStructure?: TableStructure;
}

const BigQueryTableMapping: React.FC<BigQueryTableMappingProps> = ({
  value,
  onChange,
  label,
  tableStructure
}) => {
  // Parse the current JSON value or initialize empty array if invalid
  const [mappings, setMappings] = useState<Omit<TableMapping, 'availableColumns'>[]>([]);
  
  // Generate a unique ID for new mappings
  const generateId = () => Math.random().toString(36).substr(2, 9);
  
  // Parse the value from props when it changes
  useEffect(() => {
    try {
      // Try to parse the value as JSON
      const parsedValue = value ? JSON.parse(value) : [];
      
      // Ensure each mapping has an ID
      const mappingsWithIds = parsedValue.map((mapping: any) => {
        if (!mapping.id) {
          return { ...mapping, id: generateId() };
        }
        return mapping;
      });
      
      setMappings(mappingsWithIds);
    } catch (error) {
      console.error('Error parsing table mappings JSON:', error);
      setMappings([]);
    }
  }, [value]);
  
  // Handler for adding a new mapping
  const handleAddMapping = (newMapping: Omit<TableMapping, 'id' | 'availableColumns'>) => {
    const mappingWithId = {
      ...newMapping,
      id: generateId()
    };
    
    const updatedMappings = [...mappings, mappingWithId];
    setMappings(updatedMappings);
    
    // Call the onChange with the updated JSON string
    onChange(JSON.stringify(updatedMappings));
  };
  
  // Handler for updating an existing mapping
  const handleUpdateMapping = (id: string, updatedMapping: Omit<TableMapping, 'id' | 'availableColumns'>) => {
    const updatedMappings = mappings.map(mapping => 
      mapping.id === id
        ? { ...updatedMapping, id }
        : mapping
    );
    
    setMappings(updatedMappings);
    onChange(JSON.stringify(updatedMappings));
  };
  
  // Handler for removing a mapping
  const handleRemoveMapping = (id: string) => {
    const updatedMappings = mappings.filter(mapping => mapping.id !== id);
    setMappings(updatedMappings);
    onChange(JSON.stringify(updatedMappings));
  };
  
  return (
    <div className="col-md-12">
      <Form.Group className="mb-2">
        <Form.Label>{label}</Form.Label>
        <div className="border rounded p-3 bg-light">
          <TableMappingForm
            tableMappings={mappings}
            onAdd={handleAddMapping}
            onUpdate={handleUpdateMapping}
            onRemove={handleRemoveMapping}
            tableStructure={tableStructure}
          />
        </div>

      </Form.Group>
    </div>
  );
};

export { TableMappingForm, BigQueryTableMapping };
export type { TableMapping, TableStructure };